import { gql } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { ConfigDetailView } from '~components/configurations/ConfigurationDetail/ConfigDetailView';
import { DefaultConfigurations } from '~components/configurations/useDefaultConfiguration';
import { Page } from '~components/ui/Page';
import { PageContent } from '~components/ui/PageContent';
import { PageLoader } from '~components/ui/PageLoader';
import { useConditionalPolling } from '~components/useConditionalPolling';
import { ConfigurationStatus } from '~graphql/__generated__/types';
import { HandleApiError } from '~graphql/HandleApiError';
import { ensure } from '~utils/types';
import { useCustomerConfigDetailPageQuery } from './__generated__/[id].graphql';

const POLL_INTERVAL = 10000;

export function CustomerConfigurationDetailPage() {
  const params = useParams();
  const customerHandle = ensure(params.customer);
  const configId = ensure(params.configurationId);

  const location = useLocation();
  const [showViewInPublishedState, setShowViewInPublishedState] = useState<boolean>(
    () => !!location.state?.showPublished,
  );
  const showViewInDraftState = !!location.state?.editConfig;

  const { data, loading, error, refetch, startPolling, stopPolling } =
    useCustomerConfigDetailPageQuery({
      variables: {
        customerHandle,
        configId,
      },
      pollInterval: POLL_INTERVAL,
    });

  useConditionalPolling({ startPolling, stopPolling, pollInterval: POLL_INTERVAL });

  const configuration = useMemo(() => data?.customerByHandle?.configuration, [data]);
  const customerData = useMemo(() => data?.customerByHandle, [data]);
  const isToggleEnabled = useMemo(
    () => configuration?.state === ConfigurationStatus.Published && !!configuration?.draft,
    [configuration],
  );

  const [selectedConfiguration, setSelectedConfiguration] = useState<
    typeof configuration | undefined
  >(undefined);

  useEffect(() => {
    if (!configuration) return;

    const hasDraft = !!configuration.draft;
    let initialConfig;

    if (showViewInPublishedState) {
      initialConfig = configuration;
    } else if (hasDraft || showViewInDraftState) {
      initialConfig = configuration.draft;
    } else {
      initialConfig = configuration;
    }

    setSelectedConfiguration(initialConfig);
  }, [configuration, showViewInPublishedState, showViewInDraftState]);

  useEffect(() => {
    if (location.state?.showPublished !== undefined) {
      setShowViewInPublishedState(!!location.state.showPublished);
    }
  }, [location.state?.showPublished]);

  const onConfigurationStateChange = (status: ConfigurationStatusType) => {
    if (!configuration || !isToggleEnabled) return;

    const configToSet =
      status === ConfigurationStatus.Published
        ? configuration
        : configuration.draft ?? configuration;

    if (status === ConfigurationStatus.Draft || status === ConfigurationStatus.Readytopublish) {
      setShowViewInPublishedState(false);
    }

    setSelectedConfiguration({ ...configToSet });
  };

  const refetchData = useCallback(async () => {
    await refetch({ customerHandle, configId });
  }, [refetch, customerHandle, configId]);

  const title = selectedConfiguration?.name ?? 'Configuration Info';

  const isResolvingConfigState = !loading && !selectedConfiguration && !!configuration;

  return (
    <Page title={`Configurations - ${title}`} pageName="config_detail">
      <PageContent>
        {loading || isResolvingConfigState ? (
          <PageLoader />
        ) : error ? (
          <HandleApiError error={error} />
        ) : customerData && selectedConfiguration ? (
          <ConfigDetailView
            customer={customerData}
            isDefaultConfiguration={checkCurrentConfigurationIsDefault(
              customerData?.customerDefaultConfigurations
                ? [customerData.customerDefaultConfigurations]
                : [],
              customerData?.multiClaimDefaultConfigurations ?? [],
              selectedConfiguration.id,
            )}
            configuration={selectedConfiguration}
            refetchData={refetchData}
            title={title}
            isConfigurationStatusToggleEnabled={isToggleEnabled}
            onConfigurationStateChange={onConfigurationStateChange}
          />
        ) : (
          <p>No configuration found.</p>
        )}
      </PageContent>
    </Page>
  );
}

function checkCurrentConfigurationIsDefault(
  customerDefaultConfigurations: DefaultConfigurations[] | null | undefined,
  multiClaimDefaultConfigurations: DefaultConfigurations[] | null | undefined,
  configurationId: string,
): boolean {
  const defaultConfigurationSet = new Set<string>();

  if (customerDefaultConfigurations && customerDefaultConfigurations.length > 0) {
    customerDefaultConfigurations.forEach((defConfig) => {
      defConfig.defaultConfigurations.forEach((config) => {
        defaultConfigurationSet.add(config.configurationId);
      });
    });
  }

  if (multiClaimDefaultConfigurations && multiClaimDefaultConfigurations.length > 0) {
    multiClaimDefaultConfigurations.forEach((defConfig) => {
      defConfig.defaultConfigurations.forEach((config) => {
        defaultConfigurationSet.add(config.configurationId);
      });
    });
  }
  return defaultConfigurationSet.has(configurationId);
}

CustomerConfigurationDetailPage.graphql = {
  queries: {
    CustomerConfigDetailPage: gql`
      query CustomerConfigDetailPage($customerHandle: String!, $configId: ID!) {
        customerByHandle(handle: $customerHandle) {
          id
          name
          customerDefaultConfigurations {
            id
            type
            defaultConfigurations {
              platform
              configurationId
            }
          }
          multiClaimDefaultConfigurations {
            id
            type
            defaultConfigurations {
              platform
              configurationId
            }
          }
          ...ConfigDetailView_customer
          configuration(id: $configId) {
            id
            name
            state
            draft {
              id
              name
            }
            ...ConfigDetailView_config
          }
        }
      }
    `,
  },
};
